import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Building2, MapPin, Globe, Users, Briefcase, ExternalLink, Star, Clock, Shield } from 'lucide-react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

function CompanyProfile() {
  const { t } = useLanguage();
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const { data } = await api.get(`/companies/${id}`);
        setCompany(data);
      } catch (err) {
        setError(err.response?.data?.error || t('failed_load_company'));
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="glass-card" style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-body)' }}>{t('loading_company')}</p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="glass-card" style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--accent-magenta)' }}>{error || t('company_not_found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade" style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header Card */}
      <div className="glass-card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Logo */}
          <div style={{
            width: '80px', height: '80px', borderRadius: '16px',
            background: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--glow-indigo)', flexShrink: 0
          }}>
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} style={{ width: '100%', height: '100%', borderRadius: '16px', objectFit: 'cover' }} />
            ) : (
              <Building2 size={36} color="var(--btn-text)" />
            )}
          </div>

          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '1.75rem', color: 'var(--text-heading)', letterSpacing: '-0.02em', margin: 0 }}>
                {company.name}
              </h1>
              {company.is_verified && (
                <span style={{
                  background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e',
                  fontSize: '0.7rem', fontWeight: 700, padding: '0.25rem 0.75rem',
                  borderRadius: '999px', border: '1px solid rgba(34, 197, 94, 0.3)'
                }}>
                  {t('verified_company')}
                </span>
              )}
            </div>

            {company.industry && (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>{company.industry}</p>
            )}

            <div style={{ display: 'flex', gap: '1.25rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              {company.location && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-body)', fontSize: '0.85rem' }}>
                  <MapPin size={14} /> {company.location}
                </span>
              )}
              {company.size && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-body)', fontSize: '0.85rem' }}>
                  <Users size={14} /> {company.size}
                </span>
              )}
              {company.website && (
                <a href={company.website} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--accent-primary)', fontSize: '0.85rem', textDecoration: 'none' }}>
                  <Globe size={14} /> {t('website_label')} <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>

        {company.description && (
          <p style={{ color: 'var(--text-body)', marginTop: '1.25rem', lineHeight: 1.6, fontSize: '0.95rem' }}>
            {company.description}
          </p>
        )}
      </div>

      {/* Two-column layout */}
      <div className="profile-grid grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Admins */}
        <div className="glass-card" style={{ padding: '1.5rem' }}>
          <h3 style={{ color: 'var(--text-heading)', fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Shield size={16} /> {t('team_label')}
          </h3>
          {company.admins && company.admins.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {company.admins.map((admin) => (
                <Link key={admin.user_id} to={`/profile/${admin.user_id}`} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none',
                  padding: '0.5rem', borderRadius: '10px',
                  transition: 'background 0.2s'
                }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: 'var(--glass-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', border: '1px solid var(--glass-border)'
                  }}>
                    {admin.avatar_url ? (
                      <img src={admin.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Users size={16} color="var(--text-muted)" />
                    )}
                  </div>
                  <div>
                    <p style={{ color: 'var(--text-heading)', fontSize: '0.85rem', fontWeight: 600, margin: 0 }}>
                      {admin.full_name || admin.username}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.7rem', margin: 0, textTransform: 'capitalize' }}>
                      {admin.admin_role === 'company_super_admin' ? 'Super Admin' : admin.admin_role === 'company_admin' ? 'Admin' : 'Member'}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('no_team_members')}</p>
          )}
        </div>

        {/* Open Jobs */}
        <div className="glass-card" style={{ padding: '1.5rem' }}>
          <h3 style={{ color: 'var(--text-heading)', fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Briefcase size={16} /> {t('open_positions_label')} ({company.jobs?.length || 0})
          </h3>
          {company.jobs && company.jobs.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {company.jobs.map((job) => (
                <div key={job.id} style={{
                  padding: '0.75rem', borderRadius: '10px',
                  background: 'var(--glass-bg)', border: '1px solid var(--glass-border)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <p style={{ color: 'var(--text-heading)', fontSize: '0.85rem', fontWeight: 600, margin: 0 }}>{job.title}</p>
                    {job.is_ap_exclusive && (
                      <span style={{
                        background: 'rgba(99, 102, 241, 0.15)', color: 'var(--accent-primary)',
                        fontSize: '0.6rem', fontWeight: 700, padding: '0.15rem 0.5rem',
                        borderRadius: '999px', whiteSpace: 'nowrap'
                      }}>
                        {t('ap_exclusive')}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                    {job.type && <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{job.type}</span>}
                    {job.location && <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{job.location}</span>}
                    {job.salary_range && <span style={{ color: 'var(--accent-cyan)', fontSize: '0.75rem' }}>{job.salary_range}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('no_open_positions')}</p>
          )}
        </div>
      </div>

      {/* Company Feed / Posts */}
      {company.posts && company.posts.length > 0 && (
        <div className="glass-card" style={{ padding: '1.5rem', marginTop: '1.5rem' }}>
          <h3 style={{ color: 'var(--text-heading)', fontSize: '1rem', marginBottom: '1rem' }}>{t('recent_posts')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {company.posts.map((post) => (
              <div key={post.id} style={{
                padding: '1rem', borderRadius: '12px',
                background: 'var(--glass-bg)', border: '1px solid var(--glass-border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '8px',
                    background: 'var(--glass-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', border: '1px solid var(--glass-border)'
                  }}>
                    {post.avatar_url ? (
                      <img src={post.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Users size={14} color="var(--text-muted)" />
                    )}
                  </div>
                  <span style={{ color: 'var(--text-heading)', fontSize: '0.8rem', fontWeight: 600 }}>
                    {post.full_name || post.username}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                    {new Date(post.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p style={{ color: 'var(--text-body)', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
                  {post.content?.substring(0, 300)}{post.content?.length > 300 ? '...' : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyProfile;
